import { HttpClient } from '@angular/common/http';
import { Injectable, Injector, inject } from '@angular/core';
import { ToasterService } from '@frontendlib/common/features/toaster/services/toaster.service';
import { environment } from '@environment';
import { AlertService } from '@modules/alert/services/alert.service';
import { SpaceContextStore } from '@modules/common/services/SpaceContextStore';
import { DisplayTimeZoneService } from '@modules/common/services/display-timezone.service';
import { owningTenantContext } from '@modules/common/interceptors/spaces.interceptor';
import { relativeDateToWorkDayStart } from '@modules/common/utils/date';
import { AccessLimitGateService } from '@modules/settings/services/access-limit-gate.service';
import type { FormFolderSummaryDto } from '@sharedlib/dtos/dto/forms-app/form.dto';
import type {
  FormCollectionOperation,
  FormCollectionOperationResultDTO,
} from '@sharedlib/dtos/dto/forms-app/form-collection-operation.dto';
import { firstValueFrom } from 'rxjs';
import { FormsApiService, type NativeForm } from './forms-api.service';
import { FormsWorkspaceStore } from './forms-workspace.store';

export type FormsCollectionOperation = FormCollectionOperation;
type CollectionResult = FormCollectionOperationResultDTO;
/** Boards' rail actions, with server-owned collection snapshots. One owner per Layout. */
@Injectable()
export class FormsSidebarActionsService {
  private readonly api = inject(FormsApiService);
  private readonly http = inject(HttpClient);
  private readonly store = inject(FormsWorkspaceStore);
  private readonly space = inject(SpaceContextStore);
  private readonly alert = inject(AlertService);
  private readonly injector = inject(Injector);
  private readonly toaster = inject(ToasterService);
  private readonly zone = inject(DisplayTimeZoneService);
  public confirm(operation: FormsCollectionOperation): void {
    const tenant = this.space.contextId(),
      generation = this.space.contextChanges();
    const perform = (): void => {
      if (this.space.contextId() === tenant && this.space.contextChanges() === generation)
        void this.runCollection(operation).catch(() => this.toaster.show({ text: 'Couldn’t save the change to a form. It is back as it was; try again.' }));
    };
    if (operation === 'trash-all-archived')
      this.alert.show({
        title: 'Delete item(s)',
        body: 'Every archived form will be moved to Trash.',
        rightButtons: [
          { title: 'CANCEL', close: true },
          { title: 'DELETE ALL', close: true, click: perform },
        ],
      });
    else if (operation === 'empty-trash')
      this.alert.show({
        title: 'Empty Trash',
        body: 'Every form in Trash will be permanently deleted. This can’t be undone.',
        rightButtons: [
          { title: 'CANCEL', close: true },
          { title: 'EMPTY TRASH', close: true, click: perform },
        ],
      });
    else perform();
  }
  public changeFolderState(folder: FormFolderSummaryDto, action: 'archive' | 'trash', apply: (changes: Record<string, unknown>) => void): void {
    this.alert.show({
      title: action === 'archive' ? 'Archive folder' : 'Trash folder',
      body: `${action === 'archive' ? 'Archive' : 'Move to Trash'} “${folder.title}” and its forms? Other apps are unaffected. Restore returns affected forms to their previous state.`,
      rightButtons: [
        { title: 'Cancel', close: true },
        { title: action === 'archive' ? 'Archive' : 'Trash', close: true,
          click: () => apply(action === 'archive' ? { archived: true } : { deleted: true }) },
      ],
    });
  }
  public async runCollection(operation: FormsCollectionOperation): Promise<CollectionResult> {
    const tenant = await this.begin();
    const result = await firstValueFrom(
      this.http.post<CollectionResult>(
        environment.baseUrl + '/api/forms/collection-operations',
        { operation, commandUid: crypto.randomUUID() },
        { context: owningTenantContext(tenant) },
      ),
    );
    if (this.space.contextId() === tenant) void this.store.refresh();
    return result;
  }
  public create(kind: 'form', title: string, view: string): Promise<NativeForm>;
  public create(kind: 'folder', title: string, view: string): Promise<FormFolderSummaryDto>;
  public async create(
    kind: 'form' | 'folder',
    title: string,
    view: string,
  ): Promise<NativeForm | FormFolderSummaryDto> {
    if (this.injector.get(AccessLimitGateService).blocks('createItem', this.injector))
      throw Error('This item was not created.');
    const tenant = await this.begin();
    const made = kind === 'folder' ? await this.api.createFolder(tenant, title) : await this.api.create(tenant, title);
    const state = this.rowState(view);
    let result = made;
    if (Object.keys(state).length)
      try {
        result = kind === 'folder'
          ? await this.api.patchFolder(tenant, made as FormFolderSummaryDto, state)
          : await this.api.patch(tenant, made as NativeForm, state);
      } catch {
        this.toaster.show({ text: 'Couldn’t save the change to a form. It is back as it was; try again.' });
      }
    if (this.space.contextId() === tenant) void this.store.refresh();
    return result;
  }
  /** Boards `changeBoards` (boards.store.ts:721): a failed row-state write toasts, nothing was changed. */
  public async applyRowState(item: NativeForm, view: string): Promise<NativeForm> {
    const state = this.rowState(view);
    if (!Object.keys(state).length) return item;
    try {
      const result = await this.api.patch(item.accountId, item, state);
      if (this.space.contextId() === item.accountId) void this.store.refresh();
      return result;
    } catch {
      this.toaster.show({ text: 'Couldn’t save the change to a form. It is back as it was; try again.' });
      return item;
    }
  }
  private async begin(): Promise<string> {
    const tenant = this.space.contextId(),
      generation = this.space.contextChanges();
    if (!tenant || !(await this.store.beforeLeave())) throw Error('Finish the current changes before this Forms action.');
    if (this.space.contextId() !== tenant || this.space.contextChanges() !== generation)
      throw Error('The workspace changed before this action.');
    return tenant;
  }
  private rowState(view: string): Readonly<Record<string, unknown>> {
    if (view === 'flagged') return { flagged: true };
    if (view === 'followup')
      return { followed: relativeDateToWorkDayStart('tomorrow', this.zone.timeZone()).toISOString() };
    if (view === 'snoozed') return { snoozed: new Date(Date.now() + 5 * 60_000).toISOString() };
    return {};
  }
}
